import React, { useEffect, useState } from 'react';
import { ShieldAlert, Save, RotateCcw } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { Badge } from '../../../../components/ui/Badge';
import { MODULES } from '../../../../content';
import { MODULE_ID_TO_CODE } from '../../../../content/modules';
import { loadGovernanceConfig, saveGovernanceConfig, type GovernanceConfigOverrides } from '../../../../governance';
/**
 * EnforcementSection — FG4 §Fase B UI.
 *
 * Per-module pedagogy enforcement controls. Lets an operator move a module
 * along the off → shadow → warn → enforce ramp without editing JSON.
 *
 * Owns its own load/save lifecycle (separate from GovernanceSettingsTab's
 * reducer) so the wider settings form is untouched. This keeps the FG4
 * addition strictly additive: existing settings logic continues to work
 * exactly as before.
 *
 * Schema enforcement (per-question-type) is intentionally NOT exposed yet —
 * Audit 3's roll-out plan starts with two pilot modules (REK, GET) for
 * pedagogy. Schema ramp is a separate Fase C decision once shadow data is
 * in.
 */
type Mode = 'off' | 'shadow' | 'warn' | 'enforce';
const MODE_META: Record<Mode, {
  label: string;
  description: string;
  pill: string;
}> = {
  off: {
    label: 'Off',
    description: 'Niet evalueren. Issues worden stilletjes gedropt.',
    pill: 'bg-slate-100 text-slate-700 border-slate-200'
  },
  shadow: {
    label: 'Shadow',
    description: 'Evalueren, "would block" loggen in Shadow Impact Report, NIET blokkeren.',
    pill: 'bg-violet-100 text-violet-700 border-violet-200'
  },
  warn: {
    label: 'Warn',
    description: 'Evalueren, blockers degraderen naar coach (zichtbaar, niet blokkerend).',
    pill: 'bg-amber-100 text-amber-700 border-amber-200'
  },
  enforce: {
    label: 'Enforce',
    description: 'Evalueren en blokkeren bij faal (volledige strictheid).',
    pill: 'bg-red-100 text-red-700 border-red-200'
  }
};
const MODES: Mode[] = ['off', 'shadow', 'warn', 'enforce'];
export function EnforcementSection() {
  const [modes, setModes] = useState<Record<string, Mode>>({});
  const [originalModes, setOriginalModes] = useState<Record<string, Mode>>({});
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    let cancelled = false;
    loadGovernanceConfig().then(cfg => {
      if (cancelled) return;
      const next: Record<string, Mode> = {};
      // Default = 'enforce' (matches resolvePedagogicalMode's structural default).
      MODULES.forEach(m => {
        const code = MODULE_ID_TO_CODE[m.id];
        if (!code) return;
        next[code] = cfg.enforcement?.pedagogical?.[code] as Mode ?? 'enforce';
      });
      setModes(next);
      setOriginalModes(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const hasChanges = MODULES.some(m => {
    const code = MODULE_ID_TO_CODE[m.id];
    return code && modes[code] !== originalModes[code];
  });
  const handleSave = async () => {
    const cfg: GovernanceConfigOverrides = await loadGovernanceConfig();
    const nextCfg: GovernanceConfigOverrides = {
      ...cfg,
      enforcement: {
        ...(cfg.enforcement ?? {}),
        pedagogical: {
          ...modes
        }
      }
    };
    const ok = await saveGovernanceConfig(nextCfg);
    if (ok) {
      setOriginalModes({
        ...modes
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };
  const handleReset = () => {
    setModes({
      ...originalModes
    });
  };
  return <div data-id="element-3872">
      <div className="flex items-start justify-between gap-3 mb-1" data-id="element-3873">
        <div data-id="element-3874">
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2" data-id="element-3875">
            <ShieldAlert className="w-4 h-4 text-violet-500" data-id="element-3876" />
            Enforcement Ramp
            <Badge variant="info" size="sm" data-id="element-3877">
              FG4 · Fase B
            </Badge>
          </h2>
        </div>
        {hasChanges && <div className="flex items-center gap-2 flex-shrink-0" data-id="element-3878">
            <Button variant="ghost" size="sm" onClick={handleReset} data-id="element-3879">
              <RotateCcw className="w-3.5 h-3.5 mr-1" data-id="element-3880" />
              Reset
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave} data-id="element-3881">
              <Save className="w-3.5 h-3.5 mr-1" data-id="element-3882" />
              Opslaan
            </Button>
          </div>}
        {saved && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded" data-id="element-3883">
            Opgeslagen
          </span>}
      </div>
      <p className="text-sm text-gray-500 mb-5" data-id="element-3884">
        Bepaal per module hoe streng de pedagogische contract-checks gehandhaafd
        worden. Volg de ramp{' '}
        <span className="font-mono text-[11px]" data-id="element-3885">
          off → shadow → warn → enforce
        </span>{' '}
        — schakel pas naar{' '}
        <span className="font-mono text-[11px]" data-id="element-3886">enforce</span> als de Shadow
        Impact telling acceptabel is.
      </p>

      {/* Mode legend */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5" data-id="element-3887">
        {MODES.map(m => {
        const meta = MODE_META[m];
        return <div key={m} className={`px-2.5 py-2 rounded-lg border text-[11px] ${meta.pill}`} data-id="element-3888">
              <p className="font-bold uppercase tracking-wider text-[10px]" data-id="element-3889">
                {meta.label}
              </p>
              <p className="opacity-80 leading-tight mt-0.5" data-id="element-3890">
                {meta.description}
              </p>
            </div>;
      })}
      </div>

      {/* Per-module controls */}
      <div className="space-y-2" data-id="element-3891">
        {MODULES.map(mod => {
        const code = MODULE_ID_TO_CODE[mod.id];
        if (!code) return null;
        const current = modes[code] ?? 'enforce';
        const original = originalModes[code] ?? 'enforce';
        const dirty = current !== original;
        return <div key={code} className={`flex items-center gap-3 p-3 rounded-lg border ${dirty ? 'border-amber-200 bg-amber-50/40' : 'border-gray-100 bg-white'}`} data-id="element-3892">
              <div className="min-w-0 flex-1" data-id="element-3893">
                <p className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-3894">
                  <span className="font-mono text-xs text-gray-500" data-id="element-3895">
                    {code}
                  </span>
                  {mod.title}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0" data-id="element-3896">
                {MODES.map(m => {
              const isActive = current === m;
              const meta = MODE_META[m];
              return <button key={m} type="button" onClick={() => setModes(prev => ({
                ...prev,
                [code]: m
              }))} className={`px-2.5 py-1 rounded text-[11px] font-medium border transition-colors ${isActive ? meta.pill : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`} data-id="element-3897">
                      {meta.label}
                    </button>;
            })}
              </div>
            </div>;
      })}
      </div>

      <p className="text-[11px] text-gray-500 mt-4 leading-relaxed" data-id="element-3898">
        💡 Tip: zet een module eerst op{' '}
        <span className="font-mono" data-id="element-3899">shadow</span>, draai de pipeline opnieuw via{' '}
        <em data-id="element-3900">Hervalideer</em>, en bekijk de Shadow Impact Report op het Overzicht
        tabblad. Pas als de telling acceptabel is, schakel je door naar{' '}
        <span className="font-mono" data-id="element-3901">warn</span> en uiteindelijk{' '}
        <span className="font-mono" data-id="element-3902">enforce</span>.
      </p>
    </div>;
}